/**
 * Iter-270 — Voice Recap diagnostic page.
 *
 * Real-device QA tool. Surfaces every step of the voice-recap pipeline so
 * a tester on Android Chrome / Samsung Internet / iPhone Safari can take
 * a single screenshot and prove (or disprove) the full flow works.
 *
 * Steps surfaced:
 *   1. Browser + device info (User-Agent, OS, secure context, MediaRecorder support)
 *   2. Microphone permission state (live polling)
 *   3. MediaRecorder mime negotiation
 *   4. Recording duration (wall-clock)
 *   5. Captured blob size + mime
 *   6. Upload progress + HTTP status
 *   7. Whisper transcript
 *   8. AI extraction (Claude) result
 *   9. Recap save status (POST /voice/confirm)
 *
 * Mounted at /talent/recap-diag — protected by TalentLayout auth.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Mic, Square, Upload, CheckCircle2, XCircle, Loader2, Smartphone, FileAudio } from 'lucide-react';
import api from '../../utils/api';

const PASS = (label) => ({ status: 'pass', label });
const FAIL = (label) => ({ status: 'fail', label });
const WARN = (label) => ({ status: 'warn', label });
const INFO = (label) => ({ status: 'info', label });
const PENDING = (label) => ({ status: 'pending', label });

function StepRow({ title, value, hint }) {
  const palette = {
    pass: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    fail: 'bg-rose-50 text-rose-800 border-rose-200',
    warn: 'bg-amber-50 text-amber-800 border-amber-200',
    info: 'bg-slate-50 text-slate-700 border-slate-200',
    pending: 'bg-slate-50 text-slate-500 border-slate-200',
  };
  const icon = {
    pass: <CheckCircle2 className="w-4 h-4" />,
    fail: <XCircle className="w-4 h-4" />,
    warn: <XCircle className="w-4 h-4" />,
    info: <CheckCircle2 className="w-4 h-4 opacity-50" />,
    pending: <Loader2 className="w-4 h-4 animate-spin" />,
  }[value?.status || 'pending'];
  return (
    <div className={`rounded-lg border px-3 py-2 ${palette[value?.status || 'pending']}`}>
      <div className="flex items-start gap-2">
        <div className="mt-0.5">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium uppercase tracking-wide opacity-80">{title}</div>
          <div className="text-sm font-mono break-words" data-testid={`diag-${title.replace(/\s+/g, '-').toLowerCase()}-value`}>
            {value?.label || '—'}
          </div>
          {hint && <div className="text-[11px] opacity-70 mt-1">{hint}</div>}
        </div>
      </div>
    </div>
  );
}

function pickMime() {
  const opts = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4;codecs=mp4a.40.2', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/ogg'];
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  for (const m of opts) if (MediaRecorder.isTypeSupported(m)) return m;
  return '';
}

export default function RecapDiag() {
  const [diag, setDiag] = useState({
    browser: PENDING('detecting…'),
    secureContext: PENDING(''),
    mediaRecorder: PENDING(''),
    micPermission: PENDING('not requested'),
    chosenMime: PENDING(''),
    duration: INFO('0.0s'),
    blob: PENDING(''),
    upload: PENDING('not started'),
    whisper: PENDING('not started'),
    extraction: PENDING('not started'),
    save: PENDING('not started'),
  });
  const [phase, setPhase] = useState('idle'); // idle | recording | uploading | done
  const [recapResult, setRecapResult] = useState(null);
  const [bookingOptions, setBookingOptions] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState('');
  const [bookingError, setBookingError] = useState(null);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const startedAtRef = useRef(0);
  const tickRef = useRef(null);
  const mimeRef = useRef('');

  // Step 1: browser detection on mount
  useEffect(() => {
    const ua = navigator.userAgent;
    const platform = navigator.platform || 'unknown';
    setDiag((d) => ({
      ...d,
      browser: INFO(`${platform} · ${ua.substring(0, 100)}`),
      secureContext: window.isSecureContext ? PASS('https/secure context') : FAIL('NOT secure — voice recap requires HTTPS'),
      mediaRecorder: typeof MediaRecorder !== 'undefined'
        ? PASS(`MediaRecorder OK (${pickMime() || 'no supported mime'})`)
        : FAIL('MediaRecorder not available in this browser'),
    }));
    // Permission state polling (Chromium-based, Firefox)
    pollPermission();
  }, []);

  // Load available bookings for the talent (any with clock-out)
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/talent/bookings');
        const all = Array.isArray(res?.data) ? res.data : (res?.data?.bookings || []);
        const eligible = all.filter((b) => b.status === 'completed' || b.clock_out_time || b.checkout_time);
        setBookingOptions(eligible);
        if (eligible[0]) setSelectedBooking(eligible[0].id);
        if (eligible.length === 0) setBookingError('No completed booking found for this account. Clock-out at least one shift first to test the full pipeline.');
      } catch (e) {
        setBookingError('Could not load bookings: ' + (e?.message || e));
      }
    })();
  }, []);

  const pollPermission = async () => {
    try {
      if (!navigator.permissions?.query) {
        setDiag((d) => ({ ...d, micPermission: WARN('Permissions API unavailable — will prompt on record') }));
        return;
      }
      const p = await navigator.permissions.query({ name: 'microphone' });
      const label = { granted: 'granted', denied: 'denied', prompt: 'prompt (not asked)' }[p.state] || p.state;
      const fn = p.state === 'granted' ? PASS : (p.state === 'denied' ? FAIL : WARN);
      setDiag((d) => ({ ...d, micPermission: fn(label) }));
      p.onchange = () => pollPermission();
    } catch (_) {
      setDiag((d) => ({ ...d, micPermission: WARN('cannot query — will prompt on record') }));
    }
  };

  const start = async () => {
    setRecapResult(null);
    if (!selectedBooking) { setBookingError('Pick a booking first'); return; }
    setDiag((d) => ({ ...d, upload: PENDING('not started'), whisper: PENDING('not started'), extraction: PENDING('not started'), save: PENDING('not started'), blob: PENDING('') }));
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      mimeRef.current = mime;
      setDiag((d) => ({ ...d, chosenMime: PASS(mime || '(browser default)'), micPermission: PASS('granted') }));
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const elapsedMs = Date.now() - startedAtRef.current;
        const duration = Math.max(1, Math.floor(elapsedMs / 1000));
        const finalMime = rec.mimeType || mime || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: finalMime });
        setDiag((d) => ({
          ...d,
          duration: PASS(`${(elapsedMs / 1000).toFixed(2)}s`),
          blob: blob.size > 0 ? PASS(`${blob.size.toLocaleString()} bytes · ${finalMime}`) : FAIL('blob is empty — mic produced no data'),
        }));
        await uploadBlob(blob, finalMime, duration);
      };
      rec.start(1000);
      mediaRef.current = rec;
      startedAtRef.current = Date.now();
      setPhase('recording');
      setDiag((d) => ({ ...d, duration: PENDING('recording…') }));
      tickRef.current = setInterval(() => {
        const sec = ((Date.now() - startedAtRef.current) / 1000).toFixed(1);
        setDiag((d) => ({ ...d, duration: PENDING(`${sec}s`) }));
      }, 200);
    } catch (e) {
      setDiag((d) => ({ ...d, micPermission: FAIL(`${e?.name || ''}: ${e?.message || e}`) }));
      setPhase('idle');
    }
  };

  const stop = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    try { mediaRef.current?.stop(); } catch (_) { /* noop */ }
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) { /* noop */ }
    setPhase('uploading');
  };

  const uploadBlob = async (blob, mime, duration) => {
    setDiag((d) => ({ ...d, upload: PENDING(`POSTing ${blob.size.toLocaleString()} bytes…`) }));
    try {
      const fd = new FormData();
      fd.append('booking_id', selectedBooking);
      fd.append('duration_seconds', String(duration));
      fd.append('audio', blob, `diag.${mime.includes('webm') ? 'webm' : mime.includes('mp4') ? 'm4a' : mime.includes('ogg') ? 'ogg' : 'webm'}`);
      const res = await api.post('/post-event-recap/voice', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const data = res?.data || {};
      setDiag((d) => ({
        ...d,
        upload: PASS(`HTTP 200 · server stage=${data.stage}`),
        whisper: data.transcript ? PASS(`"${data.transcript.substring(0, 140)}${data.transcript.length > 140 ? '…' : ''}"`) : FAIL(`Whisper failed: ${data.error || 'no transcript'}`),
        extraction: data.extracted ? PASS(`rating=${data.extracted.rating} · highlights=${(data.extracted.highlights || []).length} · summary chars=${(data.extracted.summary || '').length}`) : (data.warning ? WARN(data.warning) : FAIL('no extraction')),
      }));
      setRecapResult(data);
      setPhase('done');
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || String(e);
      setDiag((d) => ({ ...d, upload: FAIL(`HTTP ${e?.response?.status || '???'} · ${msg}`) }));
      setPhase('done');
    }
  };

  const confirm = async () => {
    if (!recapResult?.extracted) return;
    setDiag((d) => ({ ...d, save: PENDING('POSTing /voice/confirm…') }));
    try {
      const ex = recapResult.extracted;
      const res = await api.post('/post-event-recap/voice/confirm', {
        booking_id: selectedBooking,
        rating: ex.rating || 5,
        highlights: ex.highlights || [],
        concerns: ex.concerns || [],
        summary: ex.summary || '',
        metrics: {
          leads_count: ex.leads_count,
          qualified_leads: ex.qualified_leads,
          samples_distributed: ex.samples_distributed,
          demos_completed: ex.demos_completed,
          consumer_engagements: ex.consumer_engagements,
        },
      });
      setDiag((d) => ({ ...d, save: PASS(`confirmed_at=${res?.data?.confirmed_at || 'ok'}`) }));
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || String(e);
      setDiag((d) => ({ ...d, save: FAIL(msg) }));
    }
  };

  const supportedMimes = useMemo(() => {
    if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '(MediaRecorder unavailable)';
    return ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/ogg']
      .filter((m) => MediaRecorder.isTypeSupported(m)).join(', ') || 'none';
  }, []);

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-4" data-testid="recap-diag-page">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-cyan-700" />
            Voice Recap Diagnostics
          </CardTitle>
          <p className="text-xs text-slate-500">Real-device QA tool. Records a short clip, runs Whisper + Claude, and shows every step. Take a screenshot for proof.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <StepRow title="Browser / Device" value={diag.browser} />
          <StepRow title="Secure Context" value={diag.secureContext} hint="MediaRecorder requires HTTPS or localhost." />
          <StepRow title="MediaRecorder Support" value={diag.mediaRecorder} hint={`Supported mimes: ${supportedMimes}`} />
          <StepRow title="Microphone Permission" value={diag.micPermission} />
          <StepRow title="Chosen MIME" value={diag.chosenMime} />
          <StepRow title="Recording Duration" value={diag.duration} />
          <StepRow title="Audio Blob" value={diag.blob} />
          <StepRow title="Upload (POST /voice)" value={diag.upload} />
          <StepRow title="Whisper Transcript" value={diag.whisper} />
          <StepRow title="AI Extraction (Claude)" value={diag.extraction} />
          <StepRow title="Confirm Save (POST /voice/confirm)" value={diag.save} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileAudio className="w-5 h-5 text-cyan-700" />
            Test Controls
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {bookingError && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{bookingError}</div>}
          {bookingOptions.length > 0 && (
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-600">Booking to test against</label>
              <select
                value={selectedBooking}
                onChange={(e) => setSelectedBooking(e.target.value)}
                className="w-full text-sm border border-slate-200 rounded px-2 py-1"
                data-testid="diag-booking-select"
              >
                {bookingOptions.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.venue_name || b.shift_id?.substring(0, 8)} — {(b.shift_date || '').substring(0, 10)} · {b.status}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {phase === 'idle' && (
              <Button onClick={start} className="bg-cyan-600 hover:bg-cyan-700" disabled={!selectedBooking} data-testid="diag-start-btn">
                <Mic className="w-4 h-4 mr-2" /> Start recording
              </Button>
            )}
            {phase === 'recording' && (
              <Button onClick={stop} className="bg-rose-600 hover:bg-rose-700" data-testid="diag-stop-btn">
                <Square className="w-4 h-4 mr-2" /> Stop & upload
              </Button>
            )}
            {phase === 'done' && recapResult?.extracted && (
              <Button onClick={confirm} className="bg-emerald-600 hover:bg-emerald-700" data-testid="diag-confirm-btn">
                <Upload className="w-4 h-4 mr-2" /> Confirm + save
              </Button>
            )}
            {phase === 'done' && (
              <Button variant="outline" onClick={() => { setPhase('idle'); setRecapResult(null); }} data-testid="diag-reset-btn">
                Reset
              </Button>
            )}
          </div>
          {recapResult?.transcript && (
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs">
              <div className="font-semibold text-slate-700 mb-1">Transcript</div>
              <div className="text-slate-800 whitespace-pre-wrap" data-testid="diag-transcript">{recapResult.transcript}</div>
            </div>
          )}
          {recapResult?.extracted && (
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs space-y-1" data-testid="diag-extraction">
              <div className="font-semibold text-slate-700">AI Extraction</div>
              <div><Badge className="bg-amber-100 text-amber-800 border-amber-200">Rating</Badge> {recapResult.extracted.rating ?? '—'}</div>
              <div><Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Highlights</Badge> {(recapResult.extracted.highlights || []).join(' · ') || '—'}</div>
              <div><Badge className="bg-rose-100 text-rose-800 border-rose-200">Concerns</Badge> {(recapResult.extracted.concerns || []).join(' · ') || '—'}</div>
              <div className="pt-1"><span className="font-medium">Summary:</span> {recapResult.extracted.summary || '—'}</div>
              <div className="pt-1 text-slate-600">
                leads={recapResult.extracted.leads_count ?? '—'} · demos={recapResult.extracted.demos_completed ?? '—'} · samples={recapResult.extracted.samples_distributed ?? '—'} · engagements={recapResult.extracted.consumer_engagements ?? '—'}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-[11px] text-slate-400 text-center">
        Iter-270 · Built for real-device QA. Screenshot this page after a successful recording to verify the full pipeline.
      </p>
    </div>
  );
}
